import React from 'react';
import App from 'next/app';
import Head from 'next/head';
import { deviceDetected } from 'planoplan-ui-kit/core/device-detected';
import { IS_SERVER, IS_CLIENT } from '@constants';
import { DEFAULT_LOCALE, PRIMARY_LANG } from '@constants/langs';
import { REDIRECT_URLS } from '@constants/redirect';
import { detectLanguageAndRedirect, redirect, getLocale } from '@libs';
import { getCommonData } from '@modules/get-data';
import { wrapper } from '@store';
import { setInitialShowBanners, setSettings } from '@store/actions';
import { TrProvider, Translations } from '@libs/translations';
import { checkDownloadStandaloneFromBanner } from '@modules/effects';

class MyApp extends App {
  static async getInitialProps(appContext) {
    await detectLanguageAndRedirect(appContext);

    const { ctx, router } = appContext;
    const { store, req } = ctx;
    const { locale: routerLocale } = router;
    const translations = new Translations();

    // редирект с / на /ru для СЕО
    // вместо router.pathname используется ctx.pathname, тк при переходе по Link на клиенте
    // в router в этот момент еще старый pathname (той страницы, с которой переходим)
    if (ctx.pathname === '/' && routerLocale === DEFAULT_LOCALE) {
      redirect(
        '/ru/',
        {
          locale: PRIMARY_LANG.value,
          scroll: false,
        },
        301,
        ctx.res
      );
    }

    const locale = getLocale(routerLocale);
    const clientIp = IS_SERVER ? req.clientIp || req.ip : '';
    const url = `${ctx.asPath}`;
    const cleanUrl = url.split('?')[0];
    const queryString = url.split('?')[1] || '';
    let redirectUrl = REDIRECT_URLS.find(([urlFrom, urlTo]) => urlFrom === cleanUrl);

    //страница Webinars доступна только на русском
    if (ctx.pathname === '/webinars' && locale !== 'ru') {
      redirect(
        `/${locale}/`,
        {
          locale: locale,
          scroll: false,
        },
        301,
        ctx.res
      );
    }
    //страница Согласия на обработку доступна только на русском
    if (ctx.pathname === '/personal-data-processing' && locale !== 'ru') {
      redirect(
        `/${locale}/`,
        {
          locale: locale,
          scroll: false,
        },
        301,
        ctx.res
      );
    }

    if (redirectUrl) {
      redirectUrl = redirectUrl[1].startsWith('http') ? redirectUrl[1] : `/${locale}${redirectUrl[1]}`;
      if (queryString.length > 0) {
        redirectUrl += `?${queryString}`;
      }
      redirect(
        redirectUrl,
        {
          locale: locale,
          scroll: false,
        },
        301,
        ctx.res
      );
    }

    if (IS_SERVER) {
      deviceDetected.setSsrUserAgent(req.headers['user-agent']);

      await translations.load(locale);
    }

    await store.dispatch(setSettings({ clientIp, locale, getCtx: () => ctx }));
    await store.dispatch(getCommonData());
    store.dispatch(setInitialShowBanners(ctx.pathname));

    const { pageProps } = await App.getInitialProps(appContext);

    return { pageProps, translations };
  }

  constructor(props) {
    super(props);

    // вызывается 1 раз при первой загрузке сайта на клиенте
    if (IS_CLIENT) {
      const {
        translations: { translation },
      } = props;

      this.translations = new Translations({ translation });

      // Для запуска скачивания стендалона, при не полной загрузке сайта.
      checkDownloadStandaloneFromBanner();
    }
  }

  render() {
    const { Component, pageProps, translations, router } = this.props;
    const getLayout = Component.getLayout || ((page) => page);

    this.translations = IS_CLIENT ? this.translations : translations;

    if (IS_CLIENT) {
      window.parent.postMessage(
        JSON.stringify({
          action: 'TRANSITION_PAGE',
          payload: {
            title: pageProps?.data?.meta_title || pageProps?.data?.caption,
            asPath: router.asPath.split('?')[0],
          },
        }),
        '*'
      );
    }

    return (
      <TrProvider translations={translations}>
        <Head>
          {/* Keep viewport in next/head so it replaces Next.js's default during SSR and navigation. */}
          <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
        </Head>
        {getLayout(<Component {...pageProps} translations={this.translations} />, pageProps)}
      </TrProvider>
    );
  }
}

export default wrapper.withRedux(MyApp);
